import { isNotificationSoundEnabled } from './notificationSoundUtils'

/**
 * Simple audio service for playing notification sounds
 */
class AudioService {
    private static instance: AudioService
    private notificationSound: HTMLAudioElement | null = null
    private initialized = false
    private userInteracted = false
    private lastPlayedTime = 0
    private throttleInterval = 2000 // 1 second in milliseconds

    private constructor() {
        // Private constructor for singleton pattern
        this.initialize()

        // Set up event listeners to detect user interaction
        if (typeof window !== 'undefined') {
            const interactionEvents = ['click', 'keydown', 'touchstart']

            const handleUserInteraction = () => {
                this.userInteracted = true

                // Remove event listeners after first interaction
                interactionEvents.forEach((event) => {
                    window.removeEventListener(event, handleUserInteraction)
                })
            }

            // Add event listeners
            interactionEvents.forEach((event) => {
                window.addEventListener(event, handleUserInteraction)
            })
        }
    }

    public static getInstance(): AudioService {
        if (!AudioService.instance) {
            AudioService.instance = new AudioService()
        }
        return AudioService.instance
    }

    /**
     * Initialize the audio service with sound files
     */
    public initialize(): void {
        if (this.initialized) return

        try {
            // Pre-load notification sound
            this.notificationSound = new Audio('/sound-ding.mp3')
            this.notificationSound.preload = 'auto'
            this.notificationSound.load()

            // Mark as initialized
            this.initialized = true
        } catch (error) {
            console.error('Failed to initialize audio service:', error)
        }
    }

    /**
     * Play the notification sound with throttling
     * @returns Promise that resolves when sound plays or rejects if there's an error
     */
    public playSound(): Promise<void> {
        // The user's mute toggle is enforced here, at the one place every notification sound goes through,
        // so a caller can't forget to check it.
        if (!isNotificationSoundEnabled()) {
            return Promise.resolve()
        }

        if (!this.initialized || !this.notificationSound) {
            console.warn('Audio service not initialized')
            return Promise.reject(new Error('Audio service not initialized'))
        }

        // Check if we should throttle
        const now = Date.now()
        const timeSinceLastPlay = now - this.lastPlayedTime

        if (timeSinceLastPlay < this.throttleInterval) {
            // Too soon since last play, throttle this request
            console.log(`Sound throttled (played ${timeSinceLastPlay}ms ago)`)
            return Promise.resolve()
        }

        // Only try to play if user has interacted with the page
        if (this.userInteracted) {
            try {
                // Update last played time
                this.lastPlayedTime = now

                // Create a new audio element each time
                const soundClone = new Audio(this.notificationSound.src)
                return soundClone.play()
            } catch (err) {
                console.error('Error playing notification sound:', err)
                return Promise.reject(err)
            }
        } else {
            // If user hasn't interacted, just log a message and return a resolved promise
            console.log('Cannot play sound until user interacts with the page')
            return Promise.resolve()
        }
    }

    /**
     * Check if the audio service is initialized
     */
    public isInitialized(): boolean {
        return this.initialized
    }

    /**
     * Check if the user has interacted with the page
     */
    public hasUserInteracted(): boolean {
        return this.userInteracted
    }

    /**
     * Set the throttle interval for sound playback
     * @param milliseconds The minimum time between sound plays in milliseconds
     */
    public setThrottleInterval(milliseconds: number): void {
        if (milliseconds >= 0) {
            this.throttleInterval = milliseconds
        }
    }
}

// Export the singleton instance as a named export
export const audioService = AudioService.getInstance()
